/**
 * MCP Bridge — connects the trace-sandbox browser page to the MCP server.
 *
 * Import this module in the sandbox app to enable live MCP tool execution.
 * When the MCP server is running in live mode (npm run mcp:live), this bridge
 * connects via WebSocket and proxies tool calls to the live TraceScene.
 *
 * Usage in index.ts:
 *   import { connectMCPBridge } from './ai/mcp-bridge';
 *   connectMCPBridge(() => traceScene, () => selectedObject);
 */

import { executeTool } from './tools/tool-registry';
import '../plugins/loader'; // the tools this bridge runs
import type { ToolContext } from './agent-helpers';
import type { TraceScene } from 'trace-viewer/scene';
import type { SceneObject } from 'trace-viewer/scene';
import type { TraceViewerElement } from 'trace-viewer';
import { resolvePendingImages } from './tools/generation-tools';

const MCP_BRIDGE_PORT = 4097;

let ws: WebSocket | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let active = false;
let connected = false;
let onStateChange: (() => void) | null = null;

export type MCPBridgeState = 'off' | 'connecting' | 'connected';

/** Returns the current bridge connection state. */
export function getMCPBridgeState(): MCPBridgeState {
    if (!active) return 'off';
    return connected ? 'connected' : 'connecting';
}

/** Returns true when the bridge is actively trying to connect / connected. */
export function isMCPBridgeActive(): boolean {
    return active;
}

/** Subscribe to state changes. Returns unsubscribe function. */
export function onMCPBridgeStateChange(cb: () => void): () => void {
    onStateChange = cb;
    return () => { if (onStateChange === cb) onStateChange = null; };
}

/** Stop the bridge and close the WebSocket connection. */
export function disconnectMCPBridge(): void {
    active = false;
    connected = false;
    if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
    }
    if (ws) {
        ws.onclose = null; // prevent reconnect loop
        ws.close();
        ws = null;
    }
    console.log('[MCP Bridge] Disconnected (manual)');
    onStateChange?.();
}

export function connectMCPBridge(
    getTraceScene: () => TraceScene | null,
    getSelectedObject: () => SceneObject | null,
    getViewer: () => TraceViewerElement | null = () => null,
): void {
    // Clean up any previous connection first
    disconnectMCPBridge();
    active = true;
    onStateChange?.();

    function connect(): void {
        if (!active) return;
        // Close any lingering socket before opening a new one
        if (ws) {
            ws.onclose = null;
            ws.close();
            ws = null;
        }
        try {
            ws = new WebSocket(`ws://127.0.0.1:${MCP_BRIDGE_PORT}`);
        } catch {
            scheduleReconnect();
            return;
        }

        ws.onopen = () => {
            connected = true;
            console.log('[MCP Bridge] Connected to MCP server');
            onStateChange?.();
        };

        ws.onclose = () => {
            connected = false;
            console.log('[MCP Bridge] Disconnected');
            onStateChange?.();
            if (active) scheduleReconnect();
        };

        ws.onerror = () => {
            // onclose will fire after this
        };

        ws.onmessage = async (event) => {
            let msg: { id: string; tool: string; input: Record<string, unknown> };
            try {
                const raw = typeof event.data === 'string' ? event.data : await (event.data as Blob).text();
                msg = JSON.parse(raw) as typeof msg;
            } catch {
                return;
            }

            const traceScene = getTraceScene();
            if (!traceScene) {
                ws?.send(JSON.stringify({ id: msg.id, error: 'No scene available' }));
                return;
            }

            const ctx: ToolContext = {
                traceScene,
                viewer: getViewer() ?? undefined,
                selectedObject: getSelectedObject(),
                // Declarative image() queue — without it, bridge-driven builds leave
                // invisible Image behaviors (the flush normally runs after a turn).
                pendingImages: [],
                onSceneChanged: () => { /* UI will react via existing observers */ },
            };

            try {
                let result = await executeTool(msg.tool, msg.input, ctx);
                if (ctx.pendingImages && ctx.pendingImages.length > 0) {
                    try {
                        const summary = await resolvePendingImages(ctx);
                        result += `\n\n${summary}`;
                    } catch (err) {
                        const errorMsg = err instanceof Error ? err.message : String(err);
                        result += `\n\n(Pending image resolution failed: ${errorMsg})`;
                    }
                }
                ws?.send(JSON.stringify({ id: msg.id, result }));
            } catch (err) {
                ws?.send(JSON.stringify({
                    id: msg.id,
                    error: err instanceof Error ? err.message : String(err),
                }));
            }
        };
    }

    function scheduleReconnect(): void {
        if (reconnectTimer || !active) return;
        reconnectTimer = setTimeout(() => {
            reconnectTimer = null;
            connect();
        }, 3000);
    }

    connect();
}

// Clean up on HMR to prevent zombie WebSockets from old module instances
if (import.meta.hot) {
    import.meta.hot.dispose(() => {
        disconnectMCPBridge();
    });
}
